import { Upload } from 'lucide-react'
import type React from 'react'
import { useCallback, useState } from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { UploadModal } from './UploadModal'

const UploadButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  background: none;
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${theme.spacing.lg} ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.sm};
  font-weight: 500;
  cursor: pointer;
  transition: ${theme.transitions.default};
  height: 40px;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;

    &:hover {
      background: ${theme.colors.surface};
      border-color: ${theme.colors.border};
      box-shadow: none;
    }
  }
`

interface ActivityUploadButtonProps {
  onUploadSuccess?: (workoutId: string, workoutTitle: string, workoutDate: string) => void
  onUploadError?: (error: string) => void
  disabled?: boolean
  className?: string
  athleteId: string
}

export const ActivityUploadButton: React.FC<ActivityUploadButtonProps> = ({
  onUploadSuccess,
  onUploadError,
  disabled = false,
  className,
  athleteId,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleUploadClick = useCallback(() => {
    if (!disabled) {
      setIsModalOpen(true)
    }
  }, [disabled])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const handleModalUploadSuccess = useCallback(
    (workoutId: string, workoutTitle: string, workoutDate: string) => {
      setIsModalOpen(false)
      onUploadSuccess?.(workoutId, workoutTitle, workoutDate)
    },
    [onUploadSuccess]
  )

  const handleModalUploadError = useCallback(
    (error: string) => {
      onUploadError?.(error)
    },
    [onUploadError]
  )

  return (
    <>
      <UploadButton
        onClick={handleUploadClick}
        disabled={disabled}
        title="Upload activity file or connect device"
        className={className}
      >
        <Upload size={16} />
      </UploadButton>

      <UploadModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onUploadSuccess={handleModalUploadSuccess}
        onUploadError={handleModalUploadError}
        athleteId={athleteId}
      />
    </>
  )
}
